import * as React from "react";

import {
  toOptionsResolver,
  type FilterDefinition,
} from "@/shared/components/filters";
import { AutocompleteType } from "@/shared/components/TagAndMetadataAutocomplete";
import { useElementsForAutocompleteType } from "@/shared/components/TagAndMetadataAutocomplete/hook";
import { useDatasetTagSearch } from "@/shared/domain/datasets/hooks";
import { useOrgFileTagSearch } from "@/shared/domain/files/hooks";
import {
  type PlatformEventType,
  type TriggerTemplateCatalog,
} from "@/shared/domain/triggers";

import { triggerConditionDefinitions } from "./triggerConditionFilters";

/**
 * The fields the condition builder offers for a trigger subscribed to
 * ``events``: the catalog's scalar paths under shared roots, with the org's
 * dataset and file metadata keys and tag directories filled in.
 */
export function useTriggerConditionDefinitions(
  catalog: TriggerTemplateCatalog,
  events: readonly PlatformEventType[],
): FilterDefinition[] {
  const { elements: datasetKeys } = useElementsForAutocompleteType(
    AutocompleteType.DatasetMetadataKeys,
  );
  const { elements: fileKeys } = useElementsForAutocompleteType(
    AutocompleteType.FileMetadataKeys,
  );
  const searchDatasetTags = useDatasetTagSearch();
  const searchFileTags = useOrgFileTagSearch();
  const eventsKey = events.join(",");

  return React.useMemo(
    () =>
      triggerConditionDefinitions(
        catalog,
        eventsKey.length === 0
          ? []
          : (eventsKey.split(",") as PlatformEventType[]),
        {
          metadataKeys: { dataset: datasetKeys, file: fileKeys },
          tagOptions: {
            dataset: toOptionsResolver(searchDatasetTags),
            file: toOptionsResolver(searchFileTags),
          },
        },
      ),
    [
      catalog,
      eventsKey,
      datasetKeys,
      fileKeys,
      searchDatasetTags,
      searchFileTags,
    ],
  );
}
